import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { StockSearch, QRPrintButton, ExcelExportButton, printPositionTicket, formatLocDisplay } from "@/components/WarehouseExtras";
import { MapPin, Package, ArrowLeft, ExternalLink, Plus, Edit3, Trash2, X, Check, Printer } from "lucide-react";

const LETTERS = "ABCDEFGHIJ";

// Group cabinets by first letter (row), sorted by numeric suffix
function groupCabinetsByRow(cabinets) {
  const rows = {};
  cabinets.forEach(c => {
    const letter = c.cabinet.charAt(0);
    (rows[letter] ||= []).push(c);
  });
  Object.keys(rows).forEach(k => rows[k].sort((a, b) => a.cabinet.localeCompare(b.cabinet)));
  return Object.keys(rows).sort().map(letter => ({ letter, items: rows[letter] }));
}

function CabinetFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [code, setCode] = useState(initial?.code || "");
  const [drawers, setDrawers] = useState(initial?.drawers || 20);
  const [posRows, setPosRows] = useState(initial?.pos_rows || 5);
  const [posCols, setPosCols] = useState(initial?.pos_cols || 5);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/admin/cabinets/${initial.code}`, {
          drawers: Number(drawers), pos_rows: Number(posRows), pos_cols: Number(posCols),
        });
        toast.success(`Dollapi ${initial.code} u përditësua`);
      } else {
        await api.post("/admin/cabinets", {
          code: code.trim().toUpperCase(), drawers: Number(drawers),
          pos_rows: Number(posRows), pos_cols: Number(posCols),
        });
        toast.success(`Dollapi u shtua`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!confirm(`Fshi dollapin ${initial.code}?`)) return;
    try {
      await api.delete(`/admin/cabinets/${initial.code}`);
      toast.success("U fshi"); onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gabim");
    }
  };

  const totalPerDrawer = Number(posRows) * Number(posCols);
  const totalCapacity = Number(drawers) * totalPerDrawer;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cabinet-form-modal">
      <div className="absolute inset-0 bg-black/80" onClick={onClose} />
      <div className="relative bg-[#0B1220] border border-[#1F2B44] w-full max-w-md">
        <div className="border-b border-[#1F2B44] px-4 py-3 flex items-center justify-between">
          <div className="font-display text-lg uppercase">{isEdit ? `Ndrysho dollapin ${initial.code}` : "Shto dollap të ri"}</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">Kodi i dollapit</label>
            <input value={code} onChange={e => setCode(e.target.value)} disabled={isEdit} data-testid="cab-code-input"
              placeholder="p.sh. A07 ose E01"
              className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3 uppercase disabled:opacity-50" />
            <div className="text-[11px] text-neutral-500 mt-1">Format: shkronjë + numër (p.sh. A01, B02, E05)</div>
          </div>
          <div>
            <label className="text-xs uppercase text-neutral-400 tracking-widest">Sa sirtarë ka?</label>
            <input type="number" min="1" max="99" value={drawers} onChange={e => setDrawers(e.target.value)} data-testid="cab-drawers-input"
              className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3" />
            <div className="text-[11px] text-neutral-500 mt-1">Nga 1 deri 99 sirtarë.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase text-neutral-400 tracking-widest">Rreshtat (A-J)</label>
              <input type="number" min="1" max="10" value={posRows} onChange={e => setPosRows(e.target.value)} data-testid="cab-rows-input"
                className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3" />
              <div className="text-[11px] text-neutral-500 mt-1">1-10</div>
            </div>
            <div>
              <label className="text-xs uppercase text-neutral-400 tracking-widest">Kolonat (01-20)</label>
              <input type="number" min="1" max="20" value={posCols} onChange={e => setPosCols(e.target.value)} data-testid="cab-cols-input"
                className="mt-1 w-full h-11 bg-[#131E30] border border-[#1F2B44] px-3" />
              <div className="text-[11px] text-neutral-500 mt-1">1-20</div>
            </div>
          </div>
          <div className="hg-card p-3 text-xs bg-black/60">
            <div className="text-neutral-500 uppercase tracking-widest mb-1">Përmbledhje kapaciteti</div>
            <div>Grid për sirtar: <span className="text-[#FFD600] font-bold">{posRows}×{posCols} = {totalPerDrawer} pozicione</span></div>
            <div>Totali dollapit: <span className="text-emerald-400 font-bold">{totalCapacity} pozicione</span></div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving} data-testid="cab-save"
              className="hg-btn-primary flex-1 h-11 uppercase text-xs inline-flex items-center justify-center gap-1">
              <Check size={14} /> {saving ? "Duke ruajtur..." : "Ruaj"}
            </button>
            {isEdit && (
              <button onClick={del} data-testid="cab-delete"
                className="border-2 border-red-700 text-red-400 h-11 px-4 uppercase text-xs inline-flex items-center gap-1 hover:bg-red-900/40">
                <Trash2 size={14} /> Fshi
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WarehouseMap() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cabinets, setCabinets] = useState([]);
  const [selectedCab, setSelectedCab] = useState(null);
  const [drawers, setDrawers] = useState([]);
  const [selectedDrawer, setSelectedDrawer] = useState(null);
  const [positions, setPositions] = useState([]);
  const [drawerCfg, setDrawerCfg] = useState({ pos_rows: 5, pos_cols: 5 });
  const [posDetail, setPosDetail] = useState(null);
  const [showForm, setShowForm] = useState(null); // null | "new" | cabinetObject
  const [highlightPos, setHighlightPos] = useState(null); // e.g. "A-02" to pulse

  const loadCabinets = () => api.get("/admin/warehouse/cabinets").then(r => setCabinets(r.data.cabinets));
  useEffect(() => { loadCabinets(); }, []);

  // Handle ?loc=A03-03-A-02 deep link → auto-open cabinet, drawer, highlight position
  useEffect(() => {
    const loc = searchParams.get("loc");
    if (!loc) return;
    const parts = loc.split("-");
    if (parts.length < 4) return;
    const cab = parts[0];
    const drw = parts[1];
    const pos = parts.slice(2).join("-");
    setSelectedCab(cab);
    setSelectedDrawer(drw);
    setHighlightPos(pos);
    // clear query so refresh doesn't retrigger
    setTimeout(() => {
      searchParams.delete("loc");
      setSearchParams(searchParams, { replace: true });
    }, 300);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selectedCab) { setDrawers([]); return; }
    setSelectedDrawer(null); setPositions([]); setPosDetail(null);
    api.get(`/admin/warehouse/cabinet/${selectedCab}/drawers`).then(r => setDrawers(r.data.drawers));
  }, [selectedCab]);

  useEffect(() => {
    if (!selectedCab || !selectedDrawer) { setPositions([]); return; }
    setPosDetail(null);
    api.get(`/admin/warehouse/drawer/${selectedCab}/${selectedDrawer}`).then(r => {
      setPositions(r.data.positions);
      setDrawerCfg({ pos_rows: r.data.pos_rows || 5, pos_cols: r.data.pos_cols || 5 });
      // If we came here via deep link, open detail + scroll to positions
      if (highlightPos) {
        const p = r.data.positions.find(x => x.position === highlightPos);
        setPosDetail(p && p.occupied ? p : { position: highlightPos, empty: true });
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="wh-pos-${highlightPos}"]`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 400);
        // Clear highlight after 4s
        setTimeout(() => setHighlightPos(null), 4000);
      }
    });
    // eslint-disable-next-line
  }, [selectedCab, selectedDrawer]);

  const totalOccupied = cabinets.reduce((s, c) => s + c.occupied, 0);
  const totalCapacity = cabinets.reduce((s, c) => s + c.total, 0);
  const overallPct = totalCapacity > 0 ? Math.round((totalOccupied / totalCapacity) * 100) : 0;
  const rows = groupCabinetsByRow(cabinets);
  const maxCols = Math.max(1, ...rows.map(r => r.items.length));
  const selectedCabObj = cabinets.find(c => c.cabinet === selectedCab);

  const cabStatus = (c) => {
    if (c.occupied === 0) return "empty";
    if (c.occupied >= c.total) return "full";
    return "partial";
  };
  const drawerStatus = (d) => {
    if (d.occupied === 0) return "empty";
    if (d.occupied >= d.capacity) return "full";
    return "partial";
  };

  const onSaved = () => { setShowForm(null); loadCabinets(); if (selectedCab) api.get(`/admin/warehouse/cabinet/${selectedCab}/drawers`).then(r => setDrawers(r.data.drawers)); };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h1 className="font-display text-3xl uppercase">Dollapët — Harta e magazinës</h1>
        <div className="flex gap-2 flex-wrap">
          <ExcelExportButton />
          <button onClick={() => setShowForm("new")} data-testid="wh-add-cabinet"
            className="hg-btn-primary px-4 py-2 uppercase text-xs inline-flex items-center gap-2">
            <Plus size={14} /> Shto dollap
          </button>
        </div>
      </div>
      <div className="text-sm text-neutral-400 mb-4">
        {totalOccupied}/{totalCapacity} pozicione të zëna ({overallPct}%) • {cabinets.length} dollapë
      </div>

      <StockSearch onNavigate={({ cabinet, drawer }) => { setSelectedCab(cabinet); setSelectedDrawer(drawer); setTimeout(() => window.scrollTo({ top: 400, behavior: "smooth" }), 300); }} />

      {/* Cabinet map */}
      <div className="hg-card p-4 mb-4">
        <div className="text-xs uppercase text-neutral-400 tracking-widest mb-3 flex items-center gap-2">
          <MapPin size={14} /> 1. Zgjidh dollapin
        </div>
        <div className="space-y-2">
          {rows.map(row => (
            <div key={row.letter} className="flex items-center gap-2">
              <div className="w-6 text-neutral-500 font-bold text-sm">{row.letter}</div>
              {row.items.map(c => {
                const st = cabStatus(c);
                const isSel = selectedCab === c.cabinet;
                const pct = c.total > 0 ? Math.round((c.occupied / c.total) * 100) : 0;
                return (
                  <div key={c.cabinet} className="flex-1 min-w-0 relative group">
                    <button
                      data-testid={`wh-cab-${c.cabinet}`}
                      onClick={() => setSelectedCab(c.cabinet)}
                      className={`w-full h-16 flex flex-col items-center justify-center border transition-colors ${
                        isSel ? "bg-[#FFD600] text-black border-[#FFD600]"
                          : st === "partial" ? "bg-[#FFD600]/20 text-[#FFD600] border-[#FFD600]/40 hover:bg-[#FFD600]/30"
                          : st === "full" ? "bg-neutral-800 text-neutral-500 border-neutral-800"
                          : "bg-[#0f2f1a] text-emerald-400 border-emerald-800 hover:bg-emerald-900/40"
                      }`}
                    >
                      <div className="text-sm font-bold">{c.cabinet}</div>
                      <div className="text-[10px] opacity-80">{c.occupied}/{c.total} • {c.drawers}s</div>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setShowForm({ code: c.cabinet, drawers: c.drawers }); }}
                      data-testid={`wh-edit-${c.cabinet}`}
                      className="absolute top-0.5 right-0.5 p-1 opacity-0 group-hover:opacity-100 bg-black/60 hover:bg-black/80 rounded">
                      <Edit3 size={11} />
                    </button>
                  </div>
                );
              })}
              {/* pad row to align */}
              {Array.from({ length: Math.max(0, maxCols - row.items.length) }).map((_, i) => (
                <div key={`pad-${i}`} className="flex-1 min-w-0" />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[11px] text-neutral-400 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-500"></span>I lirë</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#FFD600]"></span>Pjesërisht i zënë</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-neutral-600"></span>Plot</span>
          <span className="text-neutral-500">Nr.sirtarëve tregohet me "Ns"</span>
        </div>
      </div>

      {/* Drawers of selected cabinet */}
      {selectedCab && (
        <div className="hg-card p-4 mb-4" data-testid="wh-drawers-panel">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs uppercase text-neutral-400 tracking-widest flex items-center gap-2">
              <Package size={14} /> 2. Sirtarët në dollapin <span className="text-[#FFD600] font-bold">{selectedCab}</span>
              {selectedCabObj && <span className="text-neutral-500">({selectedCabObj.drawers} sirtarë)</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm({ code: selectedCab, drawers: selectedCabObj?.drawers || 20 })} data-testid="wh-edit-selected"
                className="text-xs border border-[#1F2B44] px-3 py-1.5 hover:bg-[#262626] inline-flex items-center gap-1">
                <Edit3 size={12} /> Ndrysho sirtarët
              </button>
              <button onClick={() => { setSelectedCab(null); setSelectedDrawer(null); }} className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1">
                <ArrowLeft size={12} /> Kthehu
              </button>
            </div>
          </div>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {drawers.map(d => {
              const st = drawerStatus(d);
              const isSel = selectedDrawer === d.drawer;
              return (
                <button
                  key={d.drawer}
                  data-testid={`wh-drawer-${d.drawer}`}
                  onClick={() => setSelectedDrawer(d.drawer)}
                  className={`h-14 flex flex-col items-center justify-center border ${
                    isSel ? "bg-[#FFD600] text-black border-[#FFD600]"
                      : st === "partial" ? "bg-[#FFD600]/15 text-[#FFD600] border-[#FFD600]/40 hover:bg-[#FFD600]/25"
                      : st === "full" ? "bg-neutral-800 text-neutral-500 border-neutral-800"
                      : "bg-[#0f2f1a] text-emerald-400 border-emerald-800 hover:bg-emerald-900/40"
                  }`}
                >
                  <div className="text-xs font-bold">{d.drawer}</div>
                  <div className="text-[9px] opacity-80">{d.occupied}/{d.capacity}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Positions of selected drawer */}
      {selectedCab && selectedDrawer && (
        <div className="hg-card p-4" data-testid="wh-positions-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase text-neutral-400 tracking-widest flex items-center gap-2">
              <MapPin size={14} /> 3. Pozicionet — <span className="text-[#FFD600] font-bold">{selectedCab} / {selectedDrawer}</span>
            </div>
            <div className="flex gap-2">
              <QRPrintButton cabinet={selectedCab} drawer={selectedDrawer} positions={positions} />
              <button onClick={() => setSelectedDrawer(null)} className="text-xs text-neutral-400 hover:text-white inline-flex items-center gap-1">
                <ArrowLeft size={12} /> Kthehu te sirtarët
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="grid gap-1 text-[10px] font-bold" style={{ gridTemplateColumns: `auto repeat(${drawerCfg.pos_cols}, minmax(0, 1fr))` }}>
                <div></div>
                {Array.from({ length: drawerCfg.pos_cols }, (_, i) => i + 1).map(c => <div key={c} className="text-neutral-500 text-center">{String(c).padStart(2, "0")}</div>)}
                {LETTERS.slice(0, drawerCfg.pos_rows).split("").map(r => (
                  <div key={r} className="contents">
                    <div className="text-neutral-500 flex items-center justify-center pr-1">{r}</div>
                    {Array.from({ length: drawerCfg.pos_cols }, (_, i) => i + 1).map(c => {
                      const id = `${r}-${String(c).padStart(2, "0")}`;
                      const p = positions.find(x => x.position === id) || { occupied: false };
                      const isSel = posDetail?.position === id;
                      const isHl = highlightPos === id;
                      return (
                        <button
                          key={id}
                          data-testid={`wh-pos-${id}`}
                          onClick={() => setPosDetail(p.occupied ? p : { ...p, position: id, empty: true })}
                          className={`aspect-square text-[9px] font-bold border relative ${
                            isSel ? "ring-2 ring-[#FFD600] " : ""
                          } ${isHl ? "animate-pulse ring-4 ring-red-500 z-10 " : ""} ${
                            !p.occupied
                              ? "bg-emerald-800/50 text-emerald-300 border-emerald-700 hover:bg-emerald-700/60"
                              : "bg-[#FFD600]/25 text-[#FFD600] border-[#FFD600]/60 hover:bg-[#FFD600]/40"
                          }`}
                        >
                          {String(c).padStart(2, "0")}
                          {isHl && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3 text-[10px] text-neutral-400 flex-wrap">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500"></span>I lirë</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FFD600]"></span>I zënë</span>
                <span className="text-neutral-500">Grid: {drawerCfg.pos_rows}×{drawerCfg.pos_cols}</span>
              </div>
            </div>

            <div className="hg-card p-4 bg-[#131E30]" data-testid="wh-pos-detail">
              {!posDetail ? (
                <div className="text-neutral-500 text-sm py-16 text-center">Kliko një pozicion për detaje</div>
              ) : posDetail.empty || !posDetail.occupied ? (
                <div>
                  <div className="text-xs uppercase text-neutral-400 tracking-widest">Pozicioni</div>
                  <div className="font-display text-3xl text-emerald-400 mb-3">{selectedCab}-{selectedDrawer}-{posDetail.position}</div>
                  <div className="text-sm text-neutral-400 mb-3">Ky pozicion është i lirë.</div>
                  <button onClick={() => printPositionTicket({ cabinet: selectedCab, drawer: selectedDrawer, position: posDetail.position, productInfo: null })}
                    data-testid="print-empty-ticket"
                    className="hg-btn-primary inline-flex items-center gap-2 px-3 py-2 text-xs uppercase">
                    <Printer size={14} /> Printo tiketë Stok Plas
                  </button>
                </div>
              ) : (
                <div>
                  <div className="text-xs uppercase text-neutral-400 tracking-widest">Pozicioni</div>
                  <div className="font-mono text-lg text-[#FFD600] mb-3">{selectedCab}-{selectedDrawer}-{posDetail.position}</div>
                  {posDetail.product_image && (
                    <img src={mediaUrl(posDetail.product_image)} alt="" className="w-full h-40 object-contain bg-[#131E30] mb-3 border border-[#1F2B44]" />
                  )}
                  <div className="text-xs uppercase text-neutral-400 tracking-widest">Produkti</div>
                  <div className="font-semibold mb-2">{posDetail.product_name}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    {posDetail.article_number && <><div className="text-neutral-500">Artikulli</div><div>{posDetail.article_number}</div></>}
                    {posDetail.product_sku && <><div className="text-neutral-500">Made in</div><div>{posDetail.product_sku}</div></>}
                    <div className="text-neutral-500">Sasia</div><div className="text-emerald-400 font-bold">{posDetail.qty} copë</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => printPositionTicket({
                        cabinet: selectedCab, drawer: selectedDrawer, position: posDetail.position,
                        productInfo: { name: posDetail.product_name, article: posDetail.article_number || posDetail.product_sku, sku: posDetail.product_sku },
                      })}
                      data-testid="print-product-ticket"
                      className="hg-btn-primary inline-flex items-center gap-2 px-3 py-2 text-xs uppercase">
                      <Printer size={14} /> Printo Tiketa (Vendosje + Dorëzim)
                    </button>
                    <Link to={`/produkt/${posDetail.product_id}`} data-testid="wh-pos-open-product"
                      className="border-2 border-[#FFD600] text-[#FFD600] inline-flex items-center gap-2 px-3 py-2 text-xs uppercase">
                      <ExternalLink size={14} /> Hap produktin
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm === "new" && <CabinetFormModal onClose={() => setShowForm(null)} onSaved={onSaved} />}
      {showForm && typeof showForm === "object" && <CabinetFormModal initial={showForm} onClose={() => setShowForm(null)} onSaved={onSaved} />}
    </div>
  );
}
